import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getQuestions,
    createQuestion,
    updateQuestion,
    getSubmissionAnswers,
    getSubmissions,
} from "../../redux/feedback/feedbackSlice";
import { toast } from "react-toastify";
import { truncateText } from "../../helper/helper";
import useDebounce from "../../hook/useDebounce";
import Select from "react-select";
import { useLocationFilter } from "../../context/LocationContext";
import { getEventLocations } from "../../redux/eventLocation/eventLocationSlice";
import PaginationHelper from "../../helper/PaginationHelper";

const FeedbackQuestionLayer = () => {
    const dispatch = useDispatch();
    const { selectedLocation } = useLocationFilter();

    const { questions = [], total = 0 } = useSelector(
        (state) => state.feedback
    );
    const { locations = [] } = useSelector(
        (state) => state.eventLocation
    );
    const locationOptions = locations.map((loc) => ({
        value: loc.id,
        label: loc.name,
    }));
    useEffect(() => {
        dispatch(getEventLocations());
    }, [dispatch]);

    const [entries, setEntries] = useState(50);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 1000);

    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        eventLocation: null,
        question: "",
        type: "TEXT",
        options: [],
    });

    const [optionInput, setOptionInput] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        dispatch(
            getQuestions({
                page,
                limit: entries,
                search: debouncedSearch,
                locationId: selectedLocation?.value || ""
            })
        );
    }, [page, entries, debouncedSearch, selectedLocation]);

    const totalPages = Math.ceil(total / entries);


    const addOption = () => {
        const value = optionInput.trim();

        if (!value) return;

        if (formData.options.includes(value)) {
            return toast.error("Option already exists");
        }

        setFormData((prev) => ({
            ...prev,
            options: [...(prev.options || []), value],
        }));

        setOptionInput("");

        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };


    const removeOption = (index) => {
        setFormData((prev) => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = () => {
        if (!formData.eventLocation) {
            toast.error("Location is required");
            return;
        }

        if (!formData.question || !formData.question.trim()) {
            toast.error("Question is required");
            return;
        }

        if (formData.question.length > 255) {
            toast.error("Question max 255 characters");
            return;
        }

        if (!formData.type) {
            toast.error("Type is required");
            return;
        }

        if (formData.type !== "TEXT" && formData.options.length === 0) {
            toast.error("At least one option is required");
            return;
        }

        const payload = {
            eventLocationId: formData.eventLocation.value,
            question: formData.question.trim(),
            type: formData.type,
            options: formData.options,
        };

        const action = editId
            ? updateQuestion({ id: editId, data: payload })
            : createQuestion(payload);

        dispatch(action)
            .unwrap()
            .then((res) => {
                toast.success(res?.message || "Success");

                dispatch(getQuestions({
                    page,
                    limit: entries,
                    search: debouncedSearch,
                    locationId: selectedLocation?.value || ""
                }));

                setFormData({
                    eventLocation: null,
                    question: "",
                    type: "TEXT",
                    options: [],
                });

                setEditId(null);

                document.querySelector("#questionModal .btn-close")?.click();
            })
            .catch((err) => {
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong"
                );
            });
    };


    return (
        <>
            <div className="card">

                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">

                    <div className="d-flex gap-2 flex-wrap align-items-center">

                        <select
                            className="form-select w-auto"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                            <option value="200">200</option>
                        </select>

                        <input
                            className="form-control"
                            style={{ width: "200px" }}
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                    </div>

                    <button
                        className="btn btn-primary"
                        data-bs-toggle="modal"
                        data-bs-target="#questionModal"
                        onClick={() => {

                            setEditId(null);
                            setFormData({
                                eventLocationId: "",
                                question: "",
                                type: "TEXT",
                                options: [],
                            });
                        }}
                    >
                        <Icon icon="ic:baseline-plus" /> Add Question
                    </button>

                </div>

                {/* TABLE */}
                <div className="card-body p-12">
                    <div className="table-responsive">
                        <table className="table bordered-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Location</th>
                                    <th>Question</th>
                                    <th>Type</th>
                                    <th>Options</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>

                                {questions.length === 0 ? (
                                    <tr>
                                        <td colSpan="6">
                                            <div className="text-center py-24">

                                                <Icon
                                                    icon="mdi:alert-circle-outline"
                                                    style={{ fontSize: 40, color: "#adb5bd" }}
                                                />

                                                <p className="mt-2 mb-0 text-secondary-light fw-medium">
                                                    {selectedLocation
                                                        ? "No feedback available for selected location"
                                                        : "No feedback available"}
                                                </p>

                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    questions.map((q, i) => (
                                        <tr key={q.id}>
                                            <td>{(page - 1) * entries + i + 1}</td>
                                            <td>{truncateText(q.locationName, 30)}</td>
                                            <td>{truncateText(q.question, 30)}</td>
                                            <td>{truncateText(q.type)}</td>

                                            <td>
                                                {q.options?.map((opt, idx) => (
                                                    <span key={idx} className="badge bg-primary-600 me-1">
                                                        {opt}
                                                    </span>
                                                ))}
                                            </td>

                                            <td>
                                                <button
                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#questionModal"
                                                    onClick={() => {

                                                        setEditId(q.id);
                                                        setFormData({
                                                            eventLocation: locationOptions.find(
                                                                (opt) => opt.value === q.eventLocationId
                                                            ),
                                                            question: q.question,
                                                            type: q.type,
                                                            options: q.options || [],
                                                        });
                                                    }}
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}

                            </tbody>
                        </table>
                    </div>
                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={total}
                        pageSize={entries}
                    />

                </div>
            </div>

            {/* MODAL */}
            <div className="modal fade" id="questionModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>{editId ? "Edit" : "Create"} Question</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body">

                            <div className="mb-3">
                                <label>
                                    Location <span className="text-danger">*</span>
                                </label>

                                <Select
                                    options={locationOptions}
                                    value={formData.eventLocation}
                                    onChange={(val) =>
                                        setFormData({ ...formData, eventLocation: val })
                                    }
                                    placeholder="Select Location"

                                />
                            </div>

                            <div className="mb-3">
                                <label>
                                    Question <span className="text-danger">*</span>
                                </label>
                                <input
                                    className="form-control"
                                    maxLength={255}
                                    placeholder="Enter question"
                                    value={formData.question}
                                    onChange={(e) =>
                                        setFormData({ ...formData, question: e.target.value })
                                    }
                                />
                            </div>

                            <div className="mb-3">
                                <label>
                                    Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select"
                                    value={formData.type}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            type: e.target.value,
                                            options: [],
                                        })
                                    }
                                >
                                    <option value="TEXT">TEXT</option>
                                    <option value="SELECT">SELECT</option>
                                    <option value="MULTISELECT">MULTISELECT</option>
                                </select>
                            </div>
                            {formData.type !== "TEXT" && (
                                <div className="mb-3">

                                    <label>
                                        Options <span className="text-danger">*</span>
                                    </label>

                                    <div className="d-flex gap-2 mt-2">
                                        <input
                                            className="form-control"
                                            value={optionInput}
                                            ref={inputRef}
                                            placeholder="Add option"
                                            onChange={(e) => setOptionInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addOption();
                                                }
                                            }}
                                        />
                                        <button
                                            className="btn btn-success"
                                            onClick={addOption}
                                            disabled={!optionInput.trim()}
                                        >
                                            Add
                                        </button>
                                    </div>

                                    <div className="mt-3 d-flex flex-wrap gap-2">
                                        {formData.options.map((opt, i) => (
                                            <span key={i} className="badge bg-primary-600">
                                                {opt}
                                                <Icon
                                                    icon="mdi:close"
                                                    onClick={() => removeOption(i)}
                                                    style={{ cursor: "pointer", marginLeft: 6 }}
                                                />
                                            </span>
                                        ))}
                                    </div>

                                </div>
                            )}
                            <div className="text-end mt-3">
                                <button className="btn btn-primary" onClick={handleSubmit}>
                                    {editId ? "Update" : "Create"}
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FeedbackQuestionLayer;